/**
 * Order sales grid — warehouse list of all sales orders.
 *
 * Server-backed via EntityGrid over odl-app/order-sales/. Columns come from the
 * backend grid-spec descriptor. Status, priority, scope (customer/site-group),
 * and salesperson filters drive server-side filter[...] params via extraParams.
 * Stats cards (All/New/Shipped/On Hold) fetch from odl-app/order-sales/stats/
 * and recompute when active scope or status filters change; clicking a card sets
 * the status filter. Cancel and delete mutations go through the odl-app OrderSale
 * RTK Query slice (features/entities/orderSales.js); tag invalidation does not
 * reach the grid's MUI dataSource, so it refreshes after each mutation via the
 * ref captured from onGridReady.
 *
 * Parity gap vs the legacy DRF grid: filter state is no longer persisted to a
 * user preference (persistFilters was tied to useServerDataGrid); Redux store
 * still provides session-level persistence across route changes.
 *
 * Contains:
 * - OrderGrid — EntityGrid over odl-app/order-sales/ (default export)
 */

import {
  AccountBalanceWalletOutlined,
  Circle,
  FlagOutlined,
  PriorityHighOutlined,
  WarehouseOutlined,
  WifiOutlined,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { fetchOrderSalesStats } from '@sophocles/api/orderSale';
import { useUser } from '@sophocles/app';
import FilterBar from '@sophocles/components/FilterBar';
import ScopeSearchInput from '@sophocles/components/ScopeSearchInput';
import ScopeContextPickers from '@sophocles/components/ScopeSearchInput/ScopeContextPickers';
import { customerScopeProvider, siteGroupScopeProvider } from '@sophocles/components/ScopeSearchInput/scopeProviders';
import StatCard, { StatCardGroup } from '@sophocles/components/StatCard';
import { EntityGrid, MONEY_FORMAT_OPTIONS } from '@sophocles/data-grids';
import GridStatusCell from '@sophocles/data-grids/components/cells/GridStatusCell';
import { toJsonApiParams } from '@sophocles/data-grids/datasource/params';
import useColumnVisibilityModel from '@sophocles/data-grids/datasource/useColumnVisibilityModel';
import useGridQuickFilter from '@sophocles/data-grids/datasource/useGridQuickFilter';
import useGridRefreshRef from '@sophocles/data-grids/datasource/useGridRefreshRef';
import useGridStats from '@sophocles/data-grids/datasource/useGridStats';
import { useDeleteOrderSaleMutation, usePatchOrderSaleMutation } from '@sophocles/features/entities/orderSales';
import {
  getOrderSaleCancelDialog,
  getOrderSalePrimaryAction,
} from '@sophocles/features/warehouse/orders/orderSaleActions';
import { orderSaleSlice } from '@sophocles/features/warehouse/orders/slice';
import useDeleteConfirm from '@sophocles/hooks/useDeleteConfirm';
import { formatDate, formatDateTime, formatNumber } from '@sophocles/utils/formatUtils';
import OrderDetailDrawer from '@sophocles/views/sales/dialogs/OrderDetailDrawer';
import { orderStatusColor } from '@sophocles/views/sales/utils/orderStatus';

import { BOOLEAN_OPTIONS, YesNoCell } from './gridCells';

const getOrderStatusOptions = t => [
  { value: 'New', label: t('warehouse.orderStatus.new') },
  { value: 'Draft', label: t('warehouse.orderStatus.draft') },
  { value: 'Shipped', label: t('warehouse.orderStatus.shipped') },
  { value: 'Billing - On Hold', label: t('warehouse.orderStatus.billingOnHold') },
  { value: 'Complete', label: t('warehouse.orderStatus.complete') },
  { value: 'Cancelled', label: t('warehouse.orderStatus.cancelled') },
  { value: 'Deleted', label: t('warehouse.orderStatus.deleted') },
];

const getPriorityOptions = t => [
  { value: 'true', label: t('warehouse.orderGrid.priority') },
  { value: 'false', label: t('warehouse.orderGrid.notPriority') },
];

/**
 * Sales > Orders grid.
 *
 * The StatCard row and the Status chip in FilterBar both drive the
 * same filters.status redux value — clicking a card sets the chip and
 * vice versa. The card row exposes the most common segments (All, New,
 * Shipped, On Hold); the chip dropdown covers every OrderStatus.
 * Other quick filters: Priority, Has Radios. Popover: Has Prepaid,
 * External Warehouse.
 */
export default function OrderGrid() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useUser();
  const { t } = useTranslation();

  const [patchOrderSale] = usePatchOrderSaleMutation();
  const [deleteOrderSale] = useDeleteOrderSaleMutation();
  const { confirmDelete, dialogElement: deleteConfirmDialog } = useDeleteConfirm();

  const filters = useSelector(state => orderSaleSlice.selectors.selectFilters(state));

  const ORDER_STATUS_OPTIONS = useMemo(() => getOrderStatusOptions(t), [t]);
  const PRIORITY_OPTIONS = useMemo(() => getPriorityOptions(t), [t]);

  // Stable ref for the grid's refresh fn — captured from onGridReady.
  const { onGridReady: captureGridReady, refresh: refreshGrid } = useGridRefreshRef();
  // gridApiRef is shared with useGridQuickFilter so the ScopeSearchInput can
  // push search to the grid. EntityGrid mounts the grid only after the spec
  // resolves; gridReady flips on onGridReady so useGridQuickFilter re-syncs.
  const gridApiRef = useGridApiRef();
  const [gridReady, setGridReady] = useState(false);
  // Declared ahead of summaryParams below — the stat cards follow the grid's
  // committed search, not the keystrokes.
  const [searchText, setSearchText, commitSearch, committedSearch] = useGridQuickFilter(gridApiRef, gridReady);

  const [activeOrder, setActiveOrder] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [pendingCancelOrder, setPendingCancelOrder] = useState(null);
  const columnVisibility = useColumnVisibilityModel('orderGrid');

  const setFilter = useCallback(
    (key, value) => {
      dispatch(orderSaleSlice.actions.setFilters({ ...filters, [key]: value }));
    },
    [dispatch, filters],
  );

  // extraParams drives both the EntityGrid list fetch (filter[...] via
  // toJsonApiParams) and the stats fetch. New odl-app param names:
  // customer_id (not customer), site_group_id (not site_group),
  // salesperson_ids (comma-sep, not salesperson__in).
  const extraParams = useMemo(() => {
    const params = {};
    if (filters.customer) params.customer_id = filters.customer;
    if (filters.site_group) params.site_group_id = filters.site_group;
    if (Array.isArray(filters.salesperson) && filters.salesperson.length > 0) {
      params.salesperson_ids = filters.salesperson.join(',');
    }
    if (filters.status && filters.status !== 'All') {
      params.status = filters.status;
    }
    if (filters.priority === 'true' || filters.priority === 'false') {
      params.priority = filters.priority === 'true';
    }
    if (filters.has_radios === true || filters.has_radios === 'true') {
      params.has_radios = true;
    }
    if (filters.has_prepaid === true || filters.has_prepaid === 'true') {
      params.has_prepaid = true;
    }
    if (filters.external_warehouse_fulfillment === true || filters.external_warehouse_fulfillment === 'true') {
      params.external_warehouse_fulfillment = true;
    } else if (filters.external_warehouse_fulfillment === false || filters.external_warehouse_fulfillment === 'false') {
      params.external_warehouse_fulfillment = false;
    }
    return params;
  }, [filters]);

  // Stat cards respect non-status filters — and the committed search — so
  // counts reflect the list the user is looking at; the stats endpoint
  // strips filter[status] server-side.
  const summaryParams = useMemo(() => {
    const { status: _s, ...rest } = extraParams;
    if (committedSearch) rest.search = committedSearch;
    return rest;
  }, [extraParams, committedSearch]);

  const statsParams = useMemo(() => toJsonApiParams(summaryParams), [summaryParams]);
  const { stats: summary, isLoading: summaryLoading } = useGridStats(fetchOrderSalesStats, statsParams);

  const handleGridReady = useCallback(
    api => {
      captureGridReady(api);
      setGridReady(true);
    },
    [captureGridReady],
  );

  const handleRowClick = useCallback(params => {
    setActiveOrder(params.row);
    setDrawerOpen(true);
  }, []);

  const pendingCancelDialog = getOrderSaleCancelDialog(pendingCancelOrder?.status);

  const handleConfirmCancelOrder = useCallback(() => {
    if (!pendingCancelOrder?.id) return;
    patchOrderSale({ id: pendingCancelOrder.id, body: { status: 'Cancelled' } }).then(() => {
      setPendingCancelOrder(null);
      refreshGrid();
    });
  }, [patchOrderSale, pendingCancelOrder, refreshGrid]);

  // Render overrides on top of descriptor column defaults. Only intentional
  // display decisions — sizing, order, and headers come from the descriptor.
  // valueGetters join the nested _detail objects into display strings.
  const fieldOverrides = useMemo(
    () => ({
      priority: {
        // Header-less flag column: the red dot is self-explanatory and the
        // narrow pinned strip has no room for the word. The column menu still
        // labels it via the descriptor's label_key.
        renderHeader: () => null,
        width: 48,
        cellClassName: 'cell-pinned',
        renderCell: ({ value }) =>
          value ? (
            <Tooltip title={t('warehouse.orderGrid.priority')}>
              <Circle sx={{ color: 'error.main', fontSize: '1rem' }} />
            </Tooltip>
          ) : null,
      },
      id: {
        cellClassName: 'cell-pinned',
        renderCell: ({ value }) => (
          <Box sx={{ fontFamily: 'monospace', color: 'primary.main' }}>
            {value?.toString()?.padStart(5, '0') ?? value}
          </Box>
        ),
      },
      customer_detail: {
        valueGetter: (_value, row) => row.customer_detail?.name ?? '',
        renderCell: ({ value }) => (value ? <Typography sx={{ color: 'primary.main' }}>{value}</Typography> : null),
      },
      site_group_detail: {
        valueGetter: (_value, row) => row.site_group_detail?.name ?? '',
        renderCell: ({ row }) => {
          const name = row.site_group_detail?.name;
          const description = row.site_group_detail?.description;
          if (!name) return null;
          return (
            <Box sx={{ overflow: 'hidden', width: '100%' }}>
              <Typography>{name}</Typography>
              {description && (
                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                  {description}
                </Typography>
              )}
            </Box>
          );
        },
      },
      status: {
        renderCell: ({ value }) => {
          if (!value) return null;
          return <GridStatusCell label={value} color={orderStatusColor(value)} />;
        },
      },
      order_total: {
        valueFormatter: value =>
          value == null ? '' : `$${formatNumber(Number(value) || 0, user, MONEY_FORMAT_OPTIONS)}`,
      },
      order_time: {
        renderCell: ({ value }) => (value ? formatDateTime(new Date(value), user) : '-'),
      },
      date_req_by: {
        renderCell: ({ value }) => (value ? formatDate(value, user) : '-'),
      },
      salesperson_detail: {
        valueGetter: (_value, row) => {
          const d = row.salesperson_detail;
          return d ? `${d.first_name} ${d.last_name}` : '';
        },
      },
      order_user_detail: {
        valueGetter: (_value, row) => {
          const d = row.order_user_detail;
          return d ? `${d.first_name} ${d.last_name}` : '';
        },
      },
      has_prepaid: {
        renderCell: ({ value }) => <YesNoCell value={value} />,
      },
      external_warehouse_fulfillment: {
        renderCell: ({ value }) => <YesNoCell value={value} />,
      },
      should_hold_billing: {
        renderCell: ({ value }) => <YesNoCell value={value} />,
      },
      should_notify_customer: {
        renderCell: ({ value }) => <YesNoCell value={value} />,
      },
      is_invoiced: {
        renderCell: ({ value }) => <YesNoCell value={value} />,
      },
    }),
    [user, t],
  );

  // Per-row actions — bespoke column the descriptor doesn't own. Buttons stop
  // propagation so the row click (detail drawer) doesn't also fire.
  const extraColumns = useMemo(
    () => [
      {
        field: 'actions',
        headerName: t('general.actions'),
        width: 180,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        hideable: false,
        align: 'right',
        headerAlign: 'right',
        display: 'flex',
        renderCell: ({ row }) => {
          const primaryAction = getOrderSalePrimaryAction(row.status);
          return (
            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
              <Button
                size="small"
                onClick={e => {
                  e.stopPropagation();
                  navigate(`/sales/orders/${row.id}`);
                }}
              >
                {t(primaryAction.i18nKey, primaryAction.label)}
              </Button>
              {row.status === 'New' && (
                <Button
                  size="small"
                  color="error"
                  onClick={e => {
                    e.stopPropagation();
                    setPendingCancelOrder(row);
                  }}
                >
                  {t('warehouse.cancelOrder', 'Cancel Order')}
                </Button>
              )}
              {row.status === 'Draft' && (
                <Button
                  size="small"
                  color="error"
                  onClick={e => {
                    e.stopPropagation();
                    confirmDelete(row, {
                      entityLabel: t('warehouse.order'),
                      onConfirm: deletedRow => deleteOrderSale({ id: deletedRow.id }).then(() => refreshGrid()),
                    });
                  }}
                >
                  {t('actions.delete')}
                </Button>
              )}
            </Stack>
          );
        },
      },
    ],
    [t, navigate, deleteOrderSale, confirmDelete, refreshGrid],
  );

  // Inline chips: the highest-signal filters. Niche booleans live behind
  // the More-filters popover so the toolbar stays readable.
  const quickFilters = useMemo(
    () => [
      {
        key: 'status',
        label: t('general.status'),
        type: 'single',
        allLabel: t('general.all'),
        icon: <FlagOutlined sx={{ fontSize: 16 }} />,
        options: ORDER_STATUS_OPTIONS,
        value: filters.status && filters.status !== 'All' ? filters.status : null,
        onChange: value => setFilter('status', value ?? 'All'),
        renderOption: opt => <GridStatusCell label={opt.label} color={orderStatusColor(opt.value)} />,
      },
      {
        key: 'priority',
        label: t('warehouse.orderGrid.priority'),
        type: 'single',
        allLabel: t('general.any'),
        icon: <PriorityHighOutlined sx={{ fontSize: 16 }} />,
        options: PRIORITY_OPTIONS,
        value: filters.priority === 'true' || filters.priority === 'false' ? filters.priority : null,
        onChange: value => setFilter('priority', value),
      },
      {
        key: 'has_radios',
        label: t('warehouse.product.hasRadios'),
        type: 'single',
        allLabel: t('general.any'),
        icon: <WifiOutlined sx={{ fontSize: 16 }} />,
        options: BOOLEAN_OPTIONS,
        value: filters.has_radios === true ? 'true' : filters.has_radios === false ? 'false' : null,
        onChange: value => setFilter('has_radios', value === 'true' ? true : value === 'false' ? false : null),
      },
    ],
    [filters, setFilter, t, ORDER_STATUS_OPTIONS, PRIORITY_OPTIONS],
  );

  // One pair of scope handlers serves both apply surfaces — the typed
  // scope-search suggestions and the page-level context pickers — so either
  // path yields the identical state.
  const handleScopeCustomerChange = useCallback(
    selection => {
      dispatch(
        orderSaleSlice.actions.setFilters({
          ...filters,
          customer: selection?.id ?? null,
          site_group: null,
        }),
      );
    },
    [dispatch, filters],
  );
  const handleScopeSiteGroupChange = useCallback(
    selection => {
      dispatch(orderSaleSlice.actions.setFilters({ ...filters, site_group: selection?.id ?? null }));
    },
    [dispatch, filters],
  );

  const moreFilters = useMemo(
    () => [
      {
        key: 'has_prepaid',
        label: t('warehouse.orderGrid.hasPrepaid'),
        type: 'single',
        allLabel: t('general.any'),
        icon: <AccountBalanceWalletOutlined sx={{ fontSize: 16 }} />,
        options: BOOLEAN_OPTIONS,
        value: filters.has_prepaid === true ? 'true' : filters.has_prepaid === false ? 'false' : null,
        onChange: value => setFilter('has_prepaid', value === 'true' ? true : value === 'false' ? false : null),
      },
      {
        key: 'external_warehouse',
        label: t('warehouse.orderGrid.externalWarehouse'),
        type: 'single',
        allLabel: t('general.any'),
        icon: <WarehouseOutlined sx={{ fontSize: 16 }} />,
        options: BOOLEAN_OPTIONS,
        value:
          filters.external_warehouse_fulfillment === true
            ? 'true'
            : filters.external_warehouse_fulfillment === false
              ? 'false'
              : null,
        onChange: value =>
          setFilter('external_warehouse_fulfillment', value === 'true' ? true : value === 'false' ? false : null),
      },
    ],
    [filters, setFilter, t],
  );

  const handleResetAll = useCallback(() => {
    dispatch(
      orderSaleSlice.actions.setFilters({
        customer: null,
        site_group: null,
        salesperson: [],
        status: 'All',
        priority: null,
        has_radios: null,
        has_prepaid: null,
        external_warehouse_fulfillment: null,
      }),
    );
  }, [dispatch]);

  // Status card is "active" when its value matches the current status filter —
  // treating null / 'All' as the same cleared state.
  const activeStatus = filters.status && filters.status !== 'All' ? filters.status : null;

  const segments = useMemo(
    () => [
      {
        value: null,
        label: t('general.all'),
        count: summary?.all ?? 0,
        tooltip: t('warehouse.orderGrid.allOrdersTooltip'),
      },
      {
        value: 'New',
        label: t('warehouse.orderStatus.new'),
        count: summary?.new ?? 0,
        color: 'info.main',
        tooltip: t('warehouse.orderGrid.newOrdersTooltip'),
      },
      {
        value: 'Shipped',
        label: t('warehouse.orderStatus.shipped'),
        count: summary?.shipped ?? 0,
        color: 'success.main',
        tooltip: t('warehouse.orderGrid.shippedOrdersTooltip'),
      },
      {
        value: 'Billing - On Hold',
        label: t('warehouse.orderGrid.onHold'),
        count: summary?.on_hold ?? 0,
        color: 'warning.main',
        tooltip: t('warehouse.orderGrid.onHoldTooltip'),
      },
    ],
    [summary, t],
  );

  // Context pickers lead the header, RMA-list style: pick the customer, then
  // their group, and the grid and cards work within that context. The typed
  // scope search writes the same state, so a suggestion picked there fills
  // these pickers.
  const statsHeader = (
    <>
      <ScopeContextPickers
        customerTerm={t('general.customer')}
        siteGroupTerm={t('siteGroups.siteGroup')}
        customerId={filters.customer}
        siteGroupId={filters.site_group}
        onCustomerChange={handleScopeCustomerChange}
        onSiteGroupChange={handleScopeSiteGroupChange}
      />
      <StatCardGroup
        columns={{ xs: 2, sm: segments.length }}
        carouselBelow="md"
        sx={{ marginBottom: 'var(--page-gap)' }}
      >
        {segments.map(segment => {
          const isActive = activeStatus === segment.value;
          return (
            <StatCard
              key={String(segment.value)}
              title={segment.label}
              value={segment.count}
              color={segment.color}
              tooltip={segment.tooltip}
              loading={summaryLoading}
              active={isActive}
              onClick={() => setFilter('status', isActive ? 'All' : (segment.value ?? 'All'))}
            />
          );
        })}
      </StatCardGroup>
    </>
  );

  // Scope search: unified customer + site-group search with suggestion popover.
  const scopeProviders = useMemo(
    () => [
      customerScopeProvider({
        label: t('general.customer'),
        isActive: () => Boolean(filters.customer),
        applyValue: handleScopeCustomerChange,
      }),
      siteGroupScopeProvider({
        label: t('siteGroups.siteGroup'),
        getCustomerId: () => filters.customer,
        isActive: () => Boolean(filters.site_group),
        applyValue: handleScopeSiteGroupChange,
      }),
    ],
    [filters, handleScopeCustomerChange, handleScopeSiteGroupChange, t],
  );

  // No scope chips here: the page-level context pickers above the stat cards
  // are the single display of the applied customer/group. The typed scope
  // providers stay — picking a suggestion runs the same handlers and fills
  // the pickers.
  const searchSlot = (
    <ScopeSearchInput
      value={searchText}
      onValueChange={setSearchText}
      providers={scopeProviders}
      placeholder={t('warehouse.orderGrid.searchPlaceholder')}
      onSubmit={commitSearch}
    />
  );

  const toolbarContent = (
    <FilterBar quickFilters={quickFilters} moreFilters={moreFilters} onResetAll={handleResetAll} />
  );

  return (
    <>
      {deleteConfirmDialog}
      <EntityGrid
        basePath="order-sales"
        fieldOverrides={fieldOverrides}
        extraParams={extraParams}
        options={{
          searchSlot,
          persistKey: 'orderGrid',
          infiniteScroll: true,
          // A row click opens the order; entity chips inside the row must
          // not hijack it into the customer/group/user pages.
          referenceLinks: false,
          createAction: {
            label: t('warehouse.createOrder'),
            onClick: () => navigate('/sales/orders/new'),
          },
          toolbarContent,
          onRowClick: handleRowClick,
          onGridReady: handleGridReady,
          gridApiRef,
          statsHeader,
          extraColumns,
          columnVisibility,
          gridProps: { clickableRows: true },
          countNoun: t('warehouse.orderGrid.ordersCountNoun', 'orders'),
        }}
      />

      <OrderDetailDrawer
        order={activeOrder}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onMutated={refreshGrid}
      />

      <Dialog open={Boolean(pendingCancelOrder)} onClose={() => setPendingCancelOrder(null)}>
        <DialogTitle>{t(pendingCancelDialog.titleI18nKey, pendingCancelDialog.title)}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t(pendingCancelDialog.bodyI18nKey, 'This will mark the order as Cancelled.')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingCancelOrder(null)}>{t('warehouse.keepOrder', 'Keep Order')}</Button>
          <Button color="error" variant="contained" onClick={handleConfirmCancelOrder}>
            {t('warehouse.confirmCancelOrder', 'Cancel Order')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
